<template>
  <div>
    <!-- User card header -->
    <div v-if="user._id" class="bg-white rounded-xl border border-gray-200/60 mb-6">
      <div class="px-6 py-6 flex items-center gap-5">
        <div class="w-14 h-14 rounded-lg bg-[var(--color-primary-light)] text-[var(--color-primary)] flex items-center justify-center text-lg font-semibold shrink-0">
          {{ userInitials }}
        </div>
        <div class="flex-1 min-w-0">
          <h2 class="text-lg font-semibold text-gray-900 truncate">{{ user.first_name }} {{ user.last_name }}</h2>
          <p class="text-sm text-gray-400 truncate">{{ user.email }}</p>
          <div class="flex items-center gap-2 mt-2">
            <span v-if="user.is_admin" class="inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-[var(--color-primary-light)] text-[var(--color-primary)]">Admin</span>
            <span :class="['inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider', user.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400']">
              {{ user.is_active ? 'Active' : 'Inactive' }}
            </span>
          </div>
        </div>
        <div class="flex items-center gap-3 shrink-0">
          <router-link to="/users" class="btn-secondary">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            Back
          </router-link>
          <button
            v-if="$checkPermission('users.update')" @click="saveUser"
            :disabled="saving"
            class="btn-primary"
          >
            <svg v-if="!saving" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
            </svg>
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>

      <!-- Tabs -->
      <div class="px-6 border-t border-gray-100">
        <div class="flex gap-0">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            @click="activeTab = tab.key"
            :class="['relative px-5 py-3.5 text-sm font-medium transition-colors', activeTab === tab.key ? 'text-[var(--color-primary)]' : 'text-gray-400 hover:text-gray-600']"
          >
            {{ tab.label }}
            <span v-if="activeTab === tab.key" class="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--color-primary)] rounded-full" />
          </button>
        </div>
      </div>
    </div>

    <!-- Overview tab -->
    <div v-if="activeTab === 'overview'" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Basic info -->
      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100">
          <h3 class="text-[15px] font-semibold text-gray-900">Basic Information</h3>
          <p class="text-sm text-gray-400 mt-0.5">Personal details</p>
        </div>
        <div class="p-6 space-y-5">
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1.5">First Name</label>
            <input v-model="form.first_name" type="text" required class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1.5">Last Name</label>
            <input v-model="form.last_name" type="text" required class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1.5">Email</label>
            <input v-model="form.email" type="email" required class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]" />
          </div>
        </div>
      </div>

      <!-- Security -->
      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100">
          <h3 class="text-[15px] font-semibold text-gray-900">Security</h3>
          <p class="text-sm text-gray-400 mt-0.5">Leave blank to keep current password</p>
        </div>
        <div class="p-6 space-y-5">
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1.5">New Password</label>
            <PasswordInput v-model="form.password" placeholder="Enter new password" />

            <!-- Strength meter -->
            <div class="mt-3">
              <div class="flex items-center justify-between mb-1.5">
                <span class="text-xs font-medium text-gray-500">Password strength</span>
                <span :class="['text-xs font-semibold', strengthColor]">{{ strengthLabel }}</span>
              </div>
              <div class="flex gap-1.5">
                <div
                  v-for="i in 4"
                  :key="i"
                  class="h-1.5 flex-1 rounded-full transition-all duration-300"
                  :class="i <= strengthLevel ? strengthBarColor : 'bg-gray-100'"
                />
              </div>
            </div>

            <!-- Password rules -->
            <div class="mt-3 space-y-1.5">
              <div v-for="rule in passwordRules" :key="rule.label" class="flex items-center gap-2">
                <div :class="['w-4 h-4 rounded-full flex items-center justify-center shrink-0', rule.met ? 'bg-green-100' : 'bg-gray-100']">
                  <svg v-if="rule.met" class="w-2.5 h-2.5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <div v-else class="w-1.5 h-1.5 rounded-full bg-gray-300" />
                </div>
                <span :class="['text-xs', rule.met ? 'text-green-600' : 'text-gray-400']">{{ rule.label }}</span>
              </div>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1.5">Confirm Password</label>
            <PasswordInput v-model="form.confirm_password" placeholder="Confirm new password" />
            <p v-if="form.password && form.confirm_password && form.password !== form.confirm_password" class="text-xs text-red-500 mt-1.5 flex items-center gap-1">
              <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              Passwords do not match
            </p>
            <p v-else-if="form.password && form.confirm_password && form.password === form.confirm_password" class="text-xs text-green-600 mt-1.5 flex items-center gap-1">
              <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              Passwords match
            </p>
          </div>
        </div>
      </div>

      <!-- Delete user -->
      <div class="lg:col-span-2">
        <DeleteResource
            warning-title="You are deleting this user"
            warning-text="This will permanently remove the user and all their permissions."
            button-label="Delete User"
            confirm-label="I confirm this user deletion"
            :confirm-message="`Are you sure you want to delete ${user.first_name} ${user.last_name}?`"
            success-message="User deleted successfully"
            :endpoint="`/users/${userId}`"
            @deleted="$router.push('/users')"
        />
      </div>
    </div>

    <!-- Access Manager tab -->
    <div v-if="activeTab === 'permissions'">
      <!-- Account status toggles -->
      <div class="bg-white rounded-xl border border-gray-200/60 mb-6">
        <div class="px-6 py-3 border-b border-gray-100">
          <h3 class="text-[15px] font-semibold text-gray-900">Account Status</h3>
          <p class="text-sm text-gray-400 mt-0.5">Control account access and privileges</p>
        </div>
        <div class="p-6">
          <div class="flex items-center justify-between py-3">
            <div>
              <p class="text-sm font-medium text-gray-900">Active Account</p>
              <p class="text-sm text-gray-400">Enable or disable this user account</p>
            </div>
            <ToggleSwitch v-model="user.is_active" />
          </div>
          <div class="border-t border-gray-100" />
          <div class="flex items-center justify-between py-3">
            <div>
              <p class="text-sm font-medium text-gray-900">Super Admin</p>
              <p class="text-sm text-gray-400">Full access to all resources and settings</p>
            </div>
            <ToggleSwitch v-model="user.is_admin" />
          </div>
        </div>
      </div>

      <!-- Resource permissions -->
      <template v-if="!user.is_admin && resources.length">
        <div v-for="resource in resources" :key="resource.key" class="bg-white rounded-xl border border-gray-200/60 mb-4">
          <!-- Resource header -->
          <div class="px-6 py-3 border-b border-gray-100">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-[15px] font-semibold text-gray-900">{{ resource.name }}</h3>
                <p class="text-sm text-gray-400 mt-0.5">{{ resource.description || 'Manage access to this resource' }}</p>
              </div>
              <!-- Summary badges -->
              <div class="flex items-center gap-1.5">
                <span v-for="action in resource.actions" :key="action.key"
                  :class="['inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold', action.granted ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400']"
                >
                  {{ action.label }}
                </span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="p-6">
            <div class="space-y-0">
              <div v-for="(action, ai) in resource.actions" :key="action.key">
                <div v-if="ai > 0" class="border-t border-gray-100 my-1" />
                <div class="flex items-center justify-between py-3">
                  <div class="flex items-center gap-3">
                    <ToggleSwitch :model-value="action.granted" @update:model-value="toggleAction(resource, action)" />
                    <div>
                      <p class="text-sm font-medium text-gray-900">{{ action.label }}</p>
                      <p class="text-sm text-gray-400 mt-0.5">{{ actionDescription(resource, action) }}</p>
                    </div>
                  </div>

                  <!-- Items selector (only for non-create/upload actions on resources with items_required) -->
                  <button
                    v-if="action.granted && needsItems(resource, action)"
                    @click="openItemsModal(resource, action)"
                    class="inline-flex items-center gap-2 h-8 px-3 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                  >
                    <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/>
                    </svg>
                    Manage Items
                    <span v-if="!action.granted_all && action.items?.length" class="w-5 h-5 rounded-full bg-[var(--color-primary)] text-white text-[10px] font-bold flex items-center justify-center">
                      {{ action.items.length }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Items selector modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="itemsModal.open" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col animate-fade-in">
            <!-- Header -->
            <div class="flex items-center justify-between px-6 pt-6 pb-4 shrink-0">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">{{ itemsModal.resource?.name }} — {{ itemsModal.action?.label }}</h3>
                <p class="text-sm text-gray-400 mt-0.5">Select which items this user can access</p>
              </div>
              <button @click="closeItemsModal" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <!-- Tabs -->
            <div class="px-6 border-b border-gray-100 shrink-0">
              <div class="flex gap-0">
                <button @click="itemsModal.tab = 'available'" :class="['relative px-4 py-3 text-sm font-medium transition-colors', itemsModal.tab === 'available' ? 'text-[var(--color-primary)]' : 'text-gray-400 hover:text-gray-600']">
                  Available ({{ filteredAvailableItems.length }})
                  <span v-if="itemsModal.tab === 'available'" class="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--color-primary)] rounded-full" />
                </button>
                <button @click="itemsModal.tab = 'selected'" :class="['relative px-4 py-3 text-sm font-medium transition-colors', itemsModal.tab === 'selected' ? 'text-[var(--color-primary)]' : 'text-gray-400 hover:text-gray-600']">
                  Selected ({{ itemsModal.action?.items?.length || 0 }})
                  <span v-if="itemsModal.tab === 'selected'" class="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--color-primary)] rounded-full" />
                </button>
              </div>
            </div>

            <!-- Search -->
            <div class="px-6 py-3 shrink-0">
              <div class="relative">
                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input v-model="itemsModal.search" type="text" placeholder="Search items..." class="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-[var(--color-primary)]" />
              </div>
            </div>

            <!-- Items list -->
            <div class="flex-1 overflow-y-auto px-4 pb-4">
              <LoadingBlock v-if="itemsModal.loading" height="150" label="Loading items..." />

              <!-- Available tab -->
              <template v-else-if="itemsModal.tab === 'available'">
                <div v-if="filteredAvailableItems.length" class="space-y-1">
                  <div
                    v-for="item in filteredAvailableItems"
                    :key="item._id"
                    :class="['flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors', isItemSelected(item._id) ? 'bg-green-50/50' : 'hover:bg-gray-50']"
                  >
                    <div class="flex-1 min-w-0">
                      <p class="text-sm truncate" :class="isItemSelected(item._id) ? 'text-green-700' : 'text-gray-900'">{{ item.name }}</p>
                      <p v-if="item.description" class="text-xs text-gray-400 truncate">{{ item.description }}</p>
                    </div>
                    <button
                      v-if="!isItemSelected(item._id)"
                      @click="addItem(item)"
                      class="p-1.5 rounded-lg text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors shrink-0"
                    >
                      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                    <span v-else class="text-xs font-medium text-green-600 shrink-0">Added</span>
                  </div>
                </div>
                <div v-else class="py-10 text-center">
                  <p class="text-sm text-gray-400">{{ readItemsEmpty ? 'No items available' : 'No matching items' }}</p>
                  <p v-if="readItemsEmpty" class="text-xs text-gray-300 mt-1">Select items in the Read action first</p>
                </div>
              </template>

              <!-- Selected tab -->
              <template v-else>
                <div v-if="itemsModal.action?.items?.length" class="space-y-1">
                  <div
                    v-for="itemId in itemsModal.action.items"
                    :key="itemId"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50"
                  >
                    <div class="flex-1 min-w-0">
                      <p class="text-sm text-gray-900 truncate">{{ getItemName(itemId) }}</p>
                      <p v-if="getItemDescription(itemId)" class="text-xs text-gray-400 truncate">{{ getItemDescription(itemId) }}</p>
                    </div>
                    <button @click="removeItem(itemId)" class="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0">
                      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                </div>
                <div v-else class="py-10 text-center">
                  <p class="text-sm text-gray-400">No items selected</p>
                  <p class="text-xs text-gray-300 mt-1">Access will be granted but no items will be visible</p>
                </div>
              </template>
            </div>

            <!-- Footer -->
            <div class="px-4 py-3 border-t border-gray-100 flex items-center justify-between shrink-0">
              <button
                @click="toggleGrantAll({ target: { checked: !itemsModal.action?.granted_all } })"
                :class="['flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors w-auto', itemsModal.action?.granted_all ? 'bg-[var(--color-primary-light)]' : 'hover:bg-gray-50']"
              >
                <div :class="['w-5 h-5 rounded border flex items-center justify-center shrink-0', itemsModal.action?.granted_all ? 'bg-[var(--color-primary)] border-[var(--color-primary)]' : 'border-gray-300']">
                  <svg v-if="itemsModal.action?.granted_all" class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div>
                  <p class="text-sm font-medium" :class="itemsModal.action?.granted_all ? 'text-[var(--color-primary)]' : 'text-gray-900'">Grant access to all items</p>
                  <p class="text-xs text-gray-400">Ignore item selection and allow full access</p>
                </div>
              </button>
              <button @click="closeItemsModal" class="px-5 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors shrink-0">
                Done
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

  </div>
</template>

<script>
import DeleteResource from '../../components/DeleteResource.vue'
import PasswordInput from '../../components/PasswordInput.vue'
import LoadingBlock from '../../components/LoadingBlock.vue'
import ToggleSwitch from '../../components/ToggleSwitch.vue'
import api from '../../services/api.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'

export default {
  name: 'UserEdit',
  components: { DeleteResource, PasswordInput, LoadingBlock, ToggleSwitch },

  data() {
    return {
      activeTab: 'overview',
      tabs: [
        { key: 'overview', label: 'Overview' },
        { key: 'permissions', label: 'Access Manager' }
      ],
      userId: null,
      user: {},
      resources: [],
      form: {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirm_password: ''
      },
      saving: false,
      itemsModal: {
        open: false,
        tab: 'available',
        search: '',
        loading: false,
        resource: null,
        action: null,
        availableItems: []
      }
    }
  },

  computed: {
    readItemsEmpty() {
      if (!this.itemsModal.action || ['read', 'create', 'upload'].includes(this.itemsModal.action.key)) return false
      const readAction = this.itemsModal.resource?.actions?.find(a => a.key === 'read')
      if (!readAction || readAction.granted_all) return false
      return !readAction.items?.length
    },
    filteredAvailableItems() {
      let items = this.itemsModal.availableItems
      // For update/delete: filter by items selected in read action
      if (this.itemsModal.action && !['read', 'create', 'upload'].includes(this.itemsModal.action.key)) {
        const readAction = this.itemsModal.resource?.actions?.find(a => a.key === 'read')
        if (readAction && !readAction.granted_all) {
          if (!readAction.items?.length) return []
          items = items.filter(i => readAction.items.includes(i._id))
        }
      }
      if (this.itemsModal.search) {
        const q = this.itemsModal.search.toLowerCase()
        items = items.filter(i => i.name?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q))
      }
      return items
    },
    userInitials() {
      const f = this.user.first_name?.[0] || ''
      const l = this.user.last_name?.[0] || ''
      return (f + l).toUpperCase() || '?'
    },
    passwordRules() {
      const p = this.form.password || ''
      return [
        { label: 'At least 8 characters', met: p.length >= 8 },
        { label: 'One uppercase letter (A-Z)', met: /[A-Z]/.test(p) },
        { label: 'One lowercase letter (a-z)', met: /[a-z]/.test(p) },
        { label: 'One number (0-9)', met: /\d/.test(p) },
        { label: 'One special character (!@#$...)', met: /[^A-Za-z0-9]/.test(p) }
      ]
    },
    strengthLevel() {
      return this.passwordRules.filter(r => r.met).length
    },
    strengthLabel() {
      return ['', 'Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'][this.strengthLevel] || ''
    },
    strengthColor() {
      return ['', 'text-red-500', 'text-orange-500', 'text-amber-500', 'text-blue-500', 'text-green-600'][this.strengthLevel] || ''
    },
    strengthBarColor() {
      return ['', 'bg-red-400', 'bg-orange-400', 'bg-amber-400', 'bg-blue-400', 'bg-green-500'][this.strengthLevel] || 'bg-gray-100'
    }
  },

  async mounted() {
    this.userId = this.$route.params.id
    await this.loadUser()
    await this.loadPermissions()
  },

  methods: {
    async loadUser() {
      try {
        const res = await api.get(`/users/${this.userId}`)
        this.user = res.data?.user || {}
        this.form.first_name = this.user.first_name || ''
        this.form.last_name = this.user.last_name || ''
        this.form.email = this.user.email || ''
      } catch (err) {
        notifyError(err?.message || 'Failed to load user')
      }
    },

    async loadPermissions() {
      try {
        const res = await api.get(`/users/${this.userId}/permission-resources`, { silent: true })
        this.resources = this.processResources(res.data?.resources || [], this.user.permissions || {})
      } catch {
        // permissions may not be available
      }
    },

    processResources(resources, permissions) {
      for (const resource of resources) {
        resource._expanded = false
        for (const action of resource.actions) {
          const perm = permissions[resource.key]?.[action.key]
          action.granted = perm?.granted || false
          action.granted_all = perm?.granted_all || false
          action.items = perm?.items || []
        }
      }
      return resources
    },

    resolvePermissions() {
      const permissions = {}
      for (const resource of this.resources) {
        permissions[resource.key] = {}
        for (const action of resource.actions) {
          permissions[resource.key][action.key] = {
            granted: action.granted,
            granted_all: action.granted_all,
            items: action.items
          }
        }
      }
      return permissions
    },

    async saveUser() {
      if (this.form.password && this.form.password !== this.form.confirm_password) {
        notifyError('Passwords do not match')
        return
      }

      this.saving = true
      try {
        const payload = {
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          email: this.form.email,
          is_admin: this.user.is_admin,
          is_active: this.user.is_active,
          permissions: this.resolvePermissions()
        }

        if (this.form.password) {
          payload.password = this.form.password
        }

        await api.put(`/users/${this.userId}`, payload)
        notifySuccess('User updated successfully')
      } catch (err) {
        notifyError(err?.message || 'Failed to update user')
      } finally {
        this.saving = false
      }
    },

    actionDescription(resource, action) {
      const descriptions = {
        read: 'View and list resource items',
        create: 'Create new resource items',
        update: 'Edit existing resource items',
        delete: 'Remove resource items',
        upload: 'Upload files to this resource'
      }
      const base = descriptions[action.key] || `Perform ${action.label.toLowerCase()} operations`
      if (!action.granted) return base
      if (!this.needsItems(resource, action)) return base
      if (action.granted_all) return 'Access to all items'
      // Warn if non-read action has no read items to pick from
      if (!['read', 'create', 'upload'].includes(action.key)) {
        const readAction = resource.actions?.find(a => a.key === 'read')
        if (readAction && !readAction.granted_all && !readAction.items?.length) {
          return 'No items — select Read items first'
        }
      }
      return `${action.items?.length || 0} items selected`
    },

    needsItems(resource, action) {
      return resource.items_required && !['create', 'upload'].includes(action.key)
    },

    toggleAction(resource, action) {
      action.granted = !action.granted
      if (action.granted && !this.needsItems(resource, action)) {
        action.granted_all = true
      }
    },

    async openItemsModal(resource, action) {
      this.itemsModal = {
        open: true,
        tab: 'available',
        search: '',
        loading: true,
        resource,
        action,
        availableItems: []
      }
      try {
        const res = await api.get(resource.uri, { params: { format: true }, silent: true })
        this.itemsModal.availableItems = res.data?.items || res.data || []
      } catch {
        this.itemsModal.availableItems = []
      }
      this.itemsModal.loading = false
    },

    closeItemsModal() {
      this.itemsModal.open = false
    },

    isItemSelected(id) {
      return this.itemsModal.action?.items?.includes(id)
    },

    addItem(item) {
      if (!this.itemsModal.action.items) this.itemsModal.action.items = []
      if (!this.itemsModal.action.items.includes(item._id)) {
        this.itemsModal.action.items.push(item._id)
        this.itemsModal.action.granted_all = false
      }
    },

    removeItem(id) {
      this.itemsModal.action.items = this.itemsModal.action.items.filter(i => i !== id)
      // Also remove from update/delete actions if removed from read
      if (this.itemsModal.action.key === 'read') {
        for (const action of this.itemsModal.resource.actions) {
          if (action.key !== 'read' && action.items) {
            action.items = action.items.filter(i => i !== id)
          }
        }
      }
    },

    toggleGrantAll(e) {
      this.itemsModal.action.granted_all = e.target.checked
    },

    getItemName(id) {
      const item = this.itemsModal.availableItems.find(i => i._id === id)
      return item?.name || id
    },

    getItemDescription(id) {
      const item = this.itemsModal.availableItems.find(i => i._id === id)
      return item?.description || null
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
